import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING, SOURCE_PROP_2025_26, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vad reglerar alkohollagen 8 kap?", a: "8 kap. reglerar serveringstillstånd – vem som kan få det, vilka krav som ställs och vad tillståndet omfattar." },
  { q: "Vilka krav finns på tillståndshavaren?", a: "Personlig och ekonomisk lämplighet, kunskaper i alkohollagen (kunskapsprovet) och lämpliga lokaler. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna. Matservering är inte längre ett villkor för tillståndet." },
  { q: "Vad är ett stadigvarande serveringstillstånd?", a: "Tillstånd som gäller tills vidare så länge verksamheten uppfyller kraven, i motsats till tillfälligt tillstånd för en avgränsad period eller ett enstaka tillfälle." },
];

export const Route = createFileRoute("/alkohollagen-kapitel-8")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Alkohollagen kap 8" },
      ]}
      title="Alkohollagen 8 kap – serveringstillstånd"
      intro="8 kap. i alkohollagen handlar om serveringstillstånd. Det är det viktigaste kapitlet att kunna inför kunskapsprovet om du driver eller planerar driva serveringsverksamhet."
      lastReviewed={LAST_REVIEWED}
      sources={[SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING, SOURCE_PROP_2025_26]}
      sections={[
        { h2: "Vad omfattas av 8 kap?", body: "Kapitlet reglerar villkor för serveringstillstånd: vem som kan få tillstånd, vilka krav som ställs på tillståndshavaren och lokalen, samt vilka typer av tillstånd som finns (stadigvarande, tillfälligt, slutet sällskap)." },
        { h2: "Krav på sökanden (8 kap. 12 §)", body: "Sökanden ska vara personligt och ekonomiskt lämplig. Det innebär bland annat skötsamhet, ordnade ekonomiska förhållanden och kunskaper om alkohollagen genom godkänt kunskapsprov." },
        { h2: "Krav på lokal (8 kap. 14–15 §§) – uppdaterat 2026", body: "Serveringsstället ska vara lämpligt utformat ur brand-, ordnings- och säkerhetssynpunkt. Tidigare krav på eget kök och tillräckligt mångsidigt utbud av lagad eller på annat sätt tillredd mat slopades 1 juni 2026. Matservering är inte längre ett villkor för tillståndet – kommunen får inte ställa krav på meny eller kök vid prövningen." },
        { h2: "Olika typer av tillstånd", body: "Stadigvarande tillstånd gäller tills vidare. Tillfälligt tillstånd avser enstaka tidsperiod eller tillfälle. Slutet sällskap riktar sig till en avgränsad krets, t.ex. en personalfest eller bröllop." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/alkohollagen-kapitel-3", label: "Alkohollagen kapitel 3" },
          { to: "/pbi-krets", label: "PBI-krets" },
          { to: "/serveringstillstand", label: "Serveringstillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Alkohollagen 8 kap – serveringstillstånd | Alkoholprovet" },
      { name: "description", content: "Allt om alkohollagen 8 kap: krav på sökanden, lokalen och olika typer av serveringstillstånd. Sammanfattning inför kunskapsprovet." },
      { property: "og:title", content: "Alkohollagen 8 kap – serveringstillstånd" },
      { property: "og:description", content: "Krav på sökanden, lokalen och olika typer av tillstånd förklarade." },
      { property: "og:url", content: "https://alkoholprovet.se/alkohollagen-kapitel-8" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/alkohollagen-kapitel-8" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Alkohollagen kap 8", url: "https://alkoholprovet.se/alkohollagen-kapitel-8" },
      ]),
      articleJsonLd({
        url: "https://alkoholprovet.se/alkohollagen-kapitel-8",
        headline: "Alkohollagen 8 kap – serveringstillstånd | Alkoholprovet",
        description: "Allt om alkohollagen 8 kap: krav på sökanden, lokalen och olika typer av serveringstillstånd. Sammanfattning inför kunskapsprovet.",
        datePublished: "2026-01-15",
        dateModified: "2026-06-10",
      }),
    ],
  }),
});
